/**
 * Референсы Pinterest с фильтрами, выделением и тегами — общий блок двух
 * экранов: слова (`scope="type"`, `/trends/type/:id`) и всех референсов
 * подборки (`scope="topic"`, `/trends/:id/refs`, плюс фильтр «слово») и
 * карточки платья (`scope="dress"`, плюс сортировка «похожесть»).
 *
 * Фильтры и сортировка — на клиенте, по одному ответу экрана: иначе «все
 * видимые» и Shift-диапазон не определены через страницы. Цепочка: период →
 * вид → слово → библиотека → тег → сортировка; число у фишки — по текущему
 * срезу до неё. «Библиотека» — лежит ли пин хоть в одной (`in_libraries`):
 * «не в библиотеке» → выделить → «в библиотеку», и разобранное уходит из среза.
 *
 * Сетка рисуется порциями по `PAGE` плиток (06.10.26): 2749 пинов подборки
 * разом вешали вкладку — картинки и так ленивые, тяжёл был сам DOM. Порция
 * растёт, когда метка под сеткой подъезжает к экрану; смена фильтра или
 * сортировки возвращает к первой порции, перезапрос данных (тег, «в
 * библиотеку») — нет, иначе страница прыгала бы. Выделение, счётчики и
 * «все видимые» — по всему срезу, а не по нарисованной порции.
 *
 * Выделение — набор id пинов: живёт при смене фильтров (выделил «кружевные»,
 * переключил на видео, довыделил), снимается «снять», Esc и уходом с экрана;
 * пины, исчезнувшие из данных, выпадают сами. Якорь Shift-диапазона — индекс в
 * ПОКАЗАННОМ порядке, поэтому он сбрасывается при смене фильтра и сортировки.
 *
 * Мутации тегов — здесь, в одном месте: панель выделения и просмотр пина зовут
 * одни и те же (просмотр — пакетом из одного). После тега кэш экрана патчится
 * на месте (`tag_ids`), без перезапроса сотен килобайт; после «в библиотеку» —
 * перезапрашивается (`in_libraries`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Pencil } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

import {
  byKind as byKindOf,
  byLibrary as byLibraryOf,
  byWord as byWordOf,
  countBy,
  inPeriod as inPeriodOf,
  KINDS,
  kindCounts as kindCountsOf,
  LIBRARIES,
  PERIODS,
  slicePins,
  sortPins,
  type KindFilter,
  type LibraryFilter,
  type Period,
  type SortKey,
  type TagFilter,
} from "@/features/pins/model/pinSlice";
import { MoreOnScroll, PAGE } from "@/components/trends/MoreOnScroll";
import { PinGrid, type GridLayout } from "@/components/trends/PinGrid";
import { PinViewer } from "@/components/trends/PinViewer";
import { SelectionBar } from "@/components/trends/SelectionBar";
import { TagManager } from "@/components/trends/TagManager";
import { btnGhost, Chip, Empty, Panel, Segmented } from "@/components/trends/ui";
import { MAX_PAIRS } from "@/lib/fitting";
import { libraryKeys } from "@/lib/library";
import { num } from "@/lib/media";
import { useSelection } from "@/lib/selection";
import {
  blankTag,
  loadTags,
  patchPinTags,
  pinsToLibrary,
  tagKeys,
  tagPins,
  tagStyle,
  untagPins,
  type Tag,
  type TagBy,
  type TagsData,
} from "@/lib/tags";
import type { Pin, TopicWord } from "@/lib/trends";

export { sortPins };
export type { SortKey };

/** Выбор вида и сортировки помнит браузер; хранилище может быть закрыто — тогда умолчание. */
function useStored<T extends string>(key: string, allowed: readonly T[], fallback: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const got = window.localStorage.getItem(key) as T | null;
      return got && allowed.includes(got) ? got : fallback;
    } catch {
      return fallback;
    }
  });
  const set = useCallback(
    (v: T) => {
      setValue(v);
      try {
        window.localStorage.setItem(key, v);
      } catch {
        // закрытое хранилище — выбор живёт до перезагрузки
      }
    },
    [key],
  );
  return [value, set];
}

const LAYOUTS = ["masonry", "rows"] as const;
const SORTS = ["trend", "saves", "reactions", "repins", "shares", "fresh"] as const;
const DRESS_SORTS = ["similar", ...SORTS] as const;

const EMPTY_TAGS: Tag[] = [];

export function References({
  pins,
  scope,
  words,
  queryKey,
  defaultLibraryName,
  emptyText,
  title,
  fittingPath,
}: {
  pins: Pin[];
  scope: "type" | "topic" | "dress";
  words?: TopicWord[];
  /** Ключ данных экрана: патч тегов и перезапрос после «в библиотеку». */
  queryKey: readonly unknown[];
  /** Имя новой библиотеки по умолчанию; `null` — для выделения, а не одного пина. */
  defaultLibraryName: (pin: Pin | null) => string;
  emptyText: ReactNode;
  title?: ReactNode;
  /** Адрес экрана примерки для набора пинов; без него кнопок «Генерировать» нет. */
  fittingPath?: (pinIds: number[]) => string;
}) {
  const queryClient = useQueryClient();
  // Фильтры живут в адресе: ссылку можно отдать коллеге. В адрес пишутся только не-умолчания.
  const [params, setParams] = useSearchParams();
  const rawKind = params.get("kind") as KindFilter | null;
  const kind: KindFilter = rawKind && KINDS.includes(rawKind) ? rawKind : "all";
  const rawPeriod = params.get("period") as Period | null;
  const period: Period = rawPeriod && PERIODS.includes(rawPeriod) ? rawPeriod : "all";
  const rawLib = params.get("lib") as LibraryFilter | null;
  const libraryFilter: LibraryFilter = rawLib && LIBRARIES.includes(rawLib) ? rawLib : "all";
  const rawTag = params.get("tag");
  const rawWord = params.get("word");
  const setParam = useCallback(
    (key: string, value: string | null) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const setKind = (v: KindFilter) => setParam("kind", v === "all" ? null : v);
  const setLibraryFilter = (v: LibraryFilter) => setParam("lib", v === "all" ? null : v);
  const setPeriod = (v: Period) => setParam("period", v === "all" ? null : v);
  const dress = scope === "dress";
  // У платья своя сортировка («похожесть» по умолчанию) и свой ключ: у трендов остаётся «тренд».
  const [sort, setSort] = useStored<SortKey>(
    dress ? "reels.pins.sort.dress" : "reels.pins.sort",
    dress ? DRESS_SORTS : SORTS,
    dress ? "similar" : "trend",
  );
  const [layout, setLayout] = useStored<GridLayout>("reels.pins.layout", LAYOUTS, "masonry");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState<number | null>(null);
  const [lastLibraryId, setLastLibraryId] = useState<number | null>(null);
  const [managing, setManaging] = useState(false);

  const tagsQuery = useQuery({ queryKey: tagKeys.list, queryFn: loadTags });
  const tags = tagsQuery.data?.tags ?? EMPTY_TAGS;
  const tagNames = useMemo(() => new Map(tags.map((t) => [t.id, t.name])), [tags]);
  const topic = scope === "topic";
  // Чужой тег или слово (нет в данных) — как отсутствующий; пока словарь тегов не пришёл, тег не судим.
  const tagFilter: TagFilter =
    rawTag === "none"
      ? "none"
      : rawTag !== null && /^\d+$/.test(rawTag) && (!tagsQuery.isSuccess || tagNames.has(Number(rawTag)))
        ? Number(rawTag)
        : null;
  const wordFilter: number | null =
    topic && rawWord !== null && /^\d+$/.test(rawWord) && (words ?? []).some((w) => w.id === Number(rawWord))
      ? Number(rawWord)
      : null;
  const setTagFilter = (v: TagFilter) => setParam("tag", v === null ? null : String(v));
  const setWordFilter = (v: number | null) => setParam("word", v === null ? null : String(v));
  const wordNames = useMemo(
    () => (topic ? new Map((words ?? []).map((w) => [w.id, w.name])) : undefined),
    [topic, words],
  );

  // Срез по шагам: число у каждой фишки — по срезу ДО её фильтра.
  const inPeriod = useMemo(() => inPeriodOf(pins, period), [pins, period]);
  const kindCounts = useMemo(() => kindCountsOf(inPeriod), [inPeriod]);
  const byKind = useMemo(() => byKindOf(inPeriod, kind), [inPeriod, kind]);
  const wordCounts = useMemo(() => countBy(byKind, (p) => p.type_ids ?? []), [byKind]);
  const byWord = useMemo(() => byWordOf(byKind, topic, wordFilter), [byKind, topic, wordFilter]);
  const inLibraryCount = useMemo(() => byWord.filter((p) => p.in_libraries > 0).length, [byWord]);
  const byLibrary = useMemo(() => byLibraryOf(byWord, libraryFilter), [byWord, libraryFilter]);
  const tagCountsInSlice = useMemo(() => countBy(byLibrary, (p) => p.tag_ids), [byLibrary]);
  const untagged = useMemo(() => byLibrary.filter((p) => p.tag_ids.length === 0).length, [byLibrary]);
  const shown: Pin[] = useMemo(() => slicePins(byLibrary, tagFilter, sort), [byLibrary, tagFilter, sort]);
  const drawn = useMemo(() => shown.slice(0, limit), [shown, limit]);
  // Просмотр листают стрелками дальше порции — сетка дорисовывается следом.
  useEffect(() => {
    if (open !== null && open >= limit) setLimit(Math.ceil((open + 1) / PAGE) * PAGE);
  }, [open, limit]);
  const { selected, selecting, setSelecting, toggle, selectVisible, clear } = useSelection(
    pins,
    shown,
    open === null && !managing,
  );
  const detailed = useMemo(() => pins.filter((p) => p.speed !== null).length, [pins]);
  // Слова с пинами — фишки фильтра; слова без единого пина только шумят.
  const wordsWithPins = useMemo(() => {
    const all = countBy(pins, (p) => p.type_ids ?? []);
    return (words ?? []).filter((w) => all.has(w.id));
  }, [pins, words]);

  // Сколько выделенных уже несут каждый тег — «на k из N» в поповере.
  const selectedTagCounts = useMemo(
    () => countBy(pins.filter((p) => selected.has(p.id)), (p) => p.tag_ids),
    [pins, selected],
  );

  const tagMut = useMutation({
    mutationFn: ({ ids, by }: { ids: number[]; by: TagBy }) => tagPins(ids, by),
    onSuccess: (got, { ids }) => {
      queryClient.setQueryData<{ pins: Pin[] }>(queryKey, (d) => patchPinTags(d, new Set(ids), got.tag.id, true));
      if (got.tag.created)
        // Новый тег — сразу в словарь, чтобы бейдж не мелькнул «#id» до перезапроса.
        queryClient.setQueryData<TagsData>(tagKeys.list, (d) =>
          d ? { ...d, tags: [...d.tags, blankTag(got.tag.id, got.tag.name)] } : d,
        );
      void queryClient.invalidateQueries({ queryKey: tagKeys.list });
    },
  });
  const untagMut = useMutation({
    mutationFn: ({ ids, tagId }: { ids: number[]; tagId: number }) => untagPins(ids, tagId),
    onSuccess: (_got, { ids, tagId }) => {
      queryClient.setQueryData<{ pins: Pin[] }>(queryKey, (d) => patchPinTags(d, new Set(ids), tagId, false));
      void queryClient.invalidateQueries({ queryKey: tagKeys.list });
    },
  });
  const onTag = useCallback(
    (ids: number[], by: TagBy) => tagMut.mutateAsync({ ids, by }).then(() => undefined),
    [tagMut],
  );
  const onUntag = useCallback(
    (ids: number[], tagId: number) => untagMut.mutateAsync({ ids, tagId }).then(() => undefined),
    [untagMut],
  );
  const afterLibrary = (libraryId: number) => {
    setLastLibraryId(libraryId);
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: libraryKeys.list });
    void queryClient.invalidateQueries({ queryKey: libraryKeys.one(libraryId) });
  };
  const toLibrary = async (libraryId: number, _name: string, onProgress: (d: number, t: number) => void) => {
    try {
      return await pinsToLibrary([...selected], libraryId, onProgress);
    } finally {
      afterLibrary(libraryId);
    }
  };

  const close = useCallback(() => setOpen(null), []);
  const more = useCallback(() => setLimit((l) => l + PAGE), []);
  const pick = useCallback((i: number) => setOpen(i), []);
  // Сброс порции и просмотра — по ПРОИЗВЕДЁННЫМ значениям фильтров: адрес меняется и без клика
  // (ссылка, назад/вперёд, тег пропал из словаря). На первом рендере — no-op (те же значения).
  useEffect(() => {
    setLimit(PAGE);
    setOpen(null);
  }, [kind, period, libraryFilter, tagFilter, wordFilter, sort]);
  const medianOf = topic ? "подборки" : "типа";

  return (
    <Panel
      title={
        title ?? (
          <>
            Референсы <span className="tnum font-normal text-fg-faint">{pins.length}</span>
          </>
        )
      }
      annot="референсы"
    >
      {pins.length ? (
        <div className="grid gap-2 border-b border-border px-3 py-2" data-annot="фильтры референсов">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented<KindFilter>
              label="вид"
              value={kind}
              onPick={setKind}
              options={[
                { value: "all", label: `все ${kindCounts.all}` },
                { value: "image", label: `картинки ${kindCounts.image}` },
                { value: "video", label: `видео ${kindCounts.video}` },
              ]}
            />
            <Segmented<Period>
              label="опубликован"
              value={period}
              onPick={setPeriod}
              options={[
                { value: "all", label: "всё время" },
                { value: "year", label: "год" },
                { value: "quarter", label: "3 мес" },
                { value: "month", label: "месяц" },
                { value: "week", label: "неделя" },
              ]}
            />
            <Segmented<SortKey>
              label="порядок"
              value={sort}
              onPick={setSort}
              options={[
                ...(dress
                  ? [{ value: "similar" as const, label: "похожесть", title: "Сходство с фото платья (SigLIP)" }]
                  : []),
                {
                  value: "trend",
                  label: "тренд",
                  title:
                    `Репинов этой копии в день с публикации (возраст не меньше недели); «×N» — во столько раз быстрее медианы ${
                      topic ? "ПОДБОРКИ" : "типа"
                    } среди картинок/видео` +
                    (topic ? " (у того же пина на экране слова число может отличаться — срез другой)" : "") +
                    `. Посчитано у ${detailed} пинов с карточкой — у остальных нет, они в хвосте.`,
                },
                { value: "saves", label: "сохранения", title: "Сохранения картинки за всё время (все её копии)" },
                { value: "reactions", label: "лайки", title: "Лайки картинки (все её копии)" },
                { value: "repins", label: "репины", title: "Сохранения именно этой копии — из карточки пина" },
                { value: "shares", label: "пересылки", title: "Пересылки — из карточки пина" },
                { value: "fresh", label: "новые", title: "Сначала свежие по дате публикации" },
              ]}
            />
            <Segmented<GridLayout>
              label="сетка"
              value={layout}
              onPick={setLayout}
              options={[
                { value: "masonry", label: "плитка", title: "Пин целиком, плитки разной высоты" },
                { value: "rows", label: "ряды", title: "Ровные ряды одинаковых плиток 3:4" },
              ]}
            />
            <Chip
              on={selecting}
              onClick={() => setSelecting(!selecting)}
              title="Режим выделения: клик по плитке выделяет, а не открывает. Без режима — Ctrl/Cmd-клик, Shift — диапазон."
            >
              <span className="inline-flex items-center gap-1">
                <CheckSquare size={12} />
                выделять
              </span>
            </Chip>
            <button
              type="button"
              className={btnGhost}
              onClick={selectVisible}
              disabled={shown.length === 0}
              title="Добавить к выделению все пины под текущим фильтром"
            >
              выделить все видимые ({num(shown.length)})
            </button>
            {selected.size ? (
              <>
                <span className="text-[12px] tnum text-accent-text">выделено {num(selected.size)}</span>
                <button type="button" className={btnGhost} onClick={clear} title="Снять выделение (Esc)">
                  снять выделение
                </button>
              </>
            ) : null}
          </div>

          {topic && wordsWithPins.length > 1 ? (
            <div className="flex flex-wrap items-center gap-1.5" data-annot="фильтр по слову">
              <span className="text-[11.5px] text-fg-faint">слово</span>
              <Chip on={wordFilter === null} onClick={() => setWordFilter(null)}>
                все
              </Chip>
              {wordsWithPins.map((w) => (
                <Chip
                  key={w.id}
                  on={wordFilter === w.id}
                  onClick={() => setWordFilter(wordFilter === w.id ? null : w.id)}
                  {...(w.state === "ignored" ? { title: "Слово отброшено — пины собраны раньше" } : {})}
                >
                  <span className={w.state === "ignored" ? "line-through decoration-fg-faint" : ""}>{w.name}</span>{" "}
                  <span className="tnum text-fg-faint">{num(wordCounts.get(w.id) ?? 0)}</span>
                </Chip>
              ))}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-1.5" data-annot="фильтр по библиотеке">
            <span className="text-[11.5px] text-fg-faint">библиотека</span>
            <Chip on={libraryFilter === "all"} onClick={() => setLibraryFilter("all")}>
              все
            </Chip>
            <Chip
              on={libraryFilter === "in"}
              onClick={() => setLibraryFilter(libraryFilter === "in" ? "all" : "in")}
              title="Пин лежит хотя бы в одной библиотеке"
            >
              в библиотеке <span className="tnum text-fg-faint">{num(inLibraryCount)}</span>
            </Chip>
            <Chip
              on={libraryFilter === "out"}
              onClick={() => setLibraryFilter(libraryFilter === "out" ? "all" : "out")}
              title="Пин ещё ни в одной библиотеке — разобрать: выделить и «в библиотеку»"
            >
              не в библиотеке <span className="tnum text-fg-faint">{num(byWord.length - inLibraryCount)}</span>
            </Chip>
          </div>

          <div className="flex flex-wrap items-center gap-1.5" data-annot="фильтр по тегу">
            <span className="text-[11.5px] text-fg-faint">тег</span>
            <Chip on={tagFilter === null} onClick={() => setTagFilter(null)}>
              все
            </Chip>
            <Chip
              on={tagFilter === "none"}
              onClick={() => setTagFilter(tagFilter === "none" ? null : "none")}
            >
              без тега <span className="tnum text-fg-faint">{num(untagged)}</span>
            </Chip>
            {tags
              .filter((t) => (tagCountsInSlice.get(t.id) ?? 0) > 0 || tagFilter === t.id)
              .map((t) => (
                <Chip
                  key={t.id}
                  on={tagFilter === t.id}
                  onClick={() => setTagFilter(tagFilter === t.id ? null : t.id)}
                >
                  <span className="inline-flex items-center gap-1">
                    <span className="size-2 rounded-full" style={tagStyle(t.id)} />
                    {t.name} <span className="tnum text-fg-faint">{num(tagCountsInSlice.get(t.id) ?? 0)}</span>
                  </span>
                </Chip>
              ))}
            <button type="button" className={`${btnGhost} ml-auto`} onClick={() => setManaging(true)}>
              <Pencil size={12} />
              править теги
            </button>
          </div>
        </div>
      ) : null}
      {pins.length === 0 ? (
        <Empty>{emptyText}</Empty>
      ) : shown.length === 0 ? (
        <Empty>Под фильтр не попал ни один пин.</Empty>
      ) : (
        <div className={`p-3 ${selected.size ? "pb-20" : ""}`}>
          <PinGrid
            pins={drawn}
            layout={layout}
            onOpen={setOpen}
            selected={selected}
            selecting={selecting}
            onToggle={toggle}
            tagNames={tagNames}
            medianOf={medianOf}
            {...(wordNames ? { wordNames } : {})}
          />
          {drawn.length < shown.length ? (
            <MoreOnScroll
              key={limit}
              onMore={more}
              text={`показано ${num(drawn.length)} из ${num(shown.length)}`}
            />
          ) : null}
        </div>
      )}
      {open !== null && shown[open] ? (
        <PinViewer
          pins={shown}
          index={open}
          onIndex={pick}
          onClose={close}
          defaultLibraryName={defaultLibraryName(shown[open] ?? null)}
          lastLibraryId={lastLibraryId}
          onAdded={afterLibrary}
          tags={tags}
          onTag={onTag}
          onUntag={onUntag}
          {...(wordNames ? { wordNames } : {})}
          {...(fittingPath ? { fittingTo: fittingPath([shown[open].id]) } : {})}
        />
      ) : null}
      {selected.size ? (
        <SelectionBar
          count={selected.size}
          visibleCount={shown.length}
          onSelectVisible={selectVisible}
          onClear={clear}
          tags={tags}
          tagCounts={selectedTagCounts}
          onTag={(tagId) => onTag([...selected], { tagId })}
          onCreateTag={(name) => onTag([...selected], { name })}
          onUntag={(tagId) => onUntag([...selected], tagId)}
          defaultLibraryName={defaultLibraryName(null)}
          lastLibraryId={lastLibraryId}
          onToLibrary={toLibrary}
          busy={tagMut.isPending || untagMut.isPending}
          error={tagMut.error ?? untagMut.error}
          {...(fittingPath
            ? { fitting: { to: fittingPath([...selected]), count: selected.size, max: MAX_PAIRS } }
            : {})}
        />
      ) : null}
      <TagManager
        open={managing}
        onClose={() => setManaging(false)}
        onChanged={() => void queryClient.invalidateQueries({ queryKey })}
      />
    </Panel>
  );
}
